import { INGESTION_POINT_TYPE_ID } from "../data-ingestion/data.ingestion.const";
import { Workflow } from "./service/Workflow";

export const FORMLESS_FORM_ID = null as null;

export interface FormActionGroup {
    formId: number | null;
    formName: string;
    actions: Workflow.WorkflowAction[];
}

export type ActionWithStage = {
    action: Workflow.WorkflowAction;
    stage: Workflow.WorkflowStage;
};

export function getActionStableId(action: Workflow.WorkflowAction): number {
    return action.workflowActionId ?? action.id!;
}

export function getStageStableId(stage: Workflow.WorkflowStage): number {
    return stage.workflowStageId ?? stage.id!;
}

function stageSortOrderForAction(workflowProcess: Workflow.WorkflowProcess, action: Workflow.WorkflowAction): number {
    return findStageForAction(workflowProcess, action)?.sortOrder ?? 0;
}

export function buildFormActionGroups(
    workflowProcess: Workflow.WorkflowProcess,
    formNameById: Map<number, string>,
): FormActionGroup[] {
    const groupByFormId = new Map<number | null, Workflow.WorkflowAction[]>();

    for (const stage of workflowProcess.stages ?? []) {
        for (const action of stage.actions ?? []) {
            const formId =
                Number(action.actionItemTypeId) === INGESTION_POINT_TYPE_ID && action.actionItemId != null
                    ? Number(action.actionItemId)
                    : FORMLESS_FORM_ID;
            const list = groupByFormId.get(formId) ?? [];
            list.push(action);
            groupByFormId.set(formId, list);
        }
    }

    const groups: FormActionGroup[] = [];
    for (const [formId, actions] of groupByFormId.entries()) {
        const sorted = [...actions].sort((left, right) => {
            const leftStageOrder = stageSortOrderForAction(workflowProcess, left);
            const rightStageOrder = stageSortOrderForAction(workflowProcess, right);
            if (leftStageOrder !== rightStageOrder) {
                return leftStageOrder - rightStageOrder;
            }
            return (left.sortOrder ?? 0) - (right.sortOrder ?? 0);
        });
        const formName = formId == null ? "Formless" : (formNameById.get(formId) ?? `Form ${formId}`);
        groups.push({ formId, formName, actions: sorted });
    }

    groups.sort((left, right) => {
        if (left.formId == null) return 1;
        if (right.formId == null) return -1;
        return left.formName.localeCompare(right.formName, undefined, { sensitivity: "base" });
    });

    return groups;
}

export function findStageForAction(
    workflowProcess: Workflow.WorkflowProcess,
    action: Workflow.WorkflowAction,
): Workflow.WorkflowStage | undefined {
    const stageId = action.workflowStageId;
    return workflowProcess.stages?.find(
        (stage) =>
            (stage.workflowStageId != null && stage.workflowStageId === stageId) ||
            (stage.id != null && stage.id === stageId),
    );
}

export function listActionsWithStage(workflowProcess: Workflow.WorkflowProcess): ActionWithStage[] {
    const rows: ActionWithStage[] = [];
    for (const stage of workflowProcess.stages ?? []) {
        for (const action of stage.actions ?? []) {
            rows.push({ action, stage });
        }
    }
    return rows;
}

export function getAdjacentSameStageActionInFormGroup(
    group: FormActionGroup,
    workflowProcess: Workflow.WorkflowProcess,
    action: Workflow.WorkflowAction,
    direction: "up" | "down",
): Workflow.WorkflowAction | undefined {
    const stage = findStageForAction(workflowProcess, action);
    if (!stage) return undefined;

    const stageId = getStageStableId(stage);
    const sameStageInGroup = group.actions.filter((row) => {
        const rowStage = findStageForAction(workflowProcess, row);
        return rowStage && getStageStableId(rowStage) === stageId;
    });

    const index = sameStageInGroup.findIndex((row) => getActionStableId(row) === getActionStableId(action));
    if (index < 0) return undefined;

    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sameStageInGroup.length) {
        return undefined;
    }
    return sameStageInGroup[targetIndex];
}

export function swapActionSortOrderInStage(
    workflowProcess: Workflow.WorkflowProcess,
    action: Workflow.WorkflowAction,
    neighbor: Workflow.WorkflowAction,
): void {
    const stage = findStageForAction(workflowProcess, action);
    if (!stage?.actions?.length) return;

    const actionIndex = stage.actions.findIndex((row) => getActionStableId(row) === getActionStableId(action));
    const neighborIndex = stage.actions.findIndex((row) => getActionStableId(row) === getActionStableId(neighbor));
    if (actionIndex < 0 || neighborIndex < 0) return;

    const tempOrder = stage.actions[actionIndex].sortOrder;
    stage.actions[actionIndex].sortOrder = stage.actions[neighborIndex].sortOrder;
    stage.actions[neighborIndex].sortOrder = tempOrder;

    const tempRef = stage.actions[actionIndex];
    stage.actions[actionIndex] = stage.actions[neighborIndex];
    stage.actions[neighborIndex] = tempRef;
}
